import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShoppingBasket, Plus, Minus, Clock, Lock, Trash2 } from "lucide-react";

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const ORDER_STATUS = {
  pending: { label: "En attente de paiement", cls: "bg-rosebrand-pale text-rose-brand" },
  confirmed: { label: "Confirmée", cls: "bg-olive text-white" },
  preparing: { label: "En préparation", cls: "bg-olive-light text-white" },
  ready: { label: "Prête au retrait", cls: "bg-rose-brand text-white" },
  collected: { label: "Retirée", cls: "bg-secondary text-muted-foreground" },
  cancelled: { label: "Annulée", cls: "bg-secondary text-muted-foreground" },
  refunded: { label: "Annulée · Remboursée", cls: "bg-secondary text-olive" },
};

export function ShopHours({ shop, open, now }) {
  return (
    <div className={`rounded-2xl border p-5 ${open ? "bg-white border-olive/30" : "bg-rosebrand-pale/60 border-rose-brand/30"}`} data-testid="shop-status-banner">
      <div className="flex items-center gap-3">
        {open ? <Clock className="w-6 h-6 text-olive" /> : <Lock className="w-6 h-6 text-rose-brand" />}
        <div>
          <p className={`font-semibold ${open ? "text-olive" : "text-rose-brand"}`} data-testid="shop-status-text">
            {open ? "Drive ouvert — commandez maintenant" : "Drive fermé pour le moment"}
          </p>
          <p className="text-xs text-muted-foreground">Heure de Paris : {now}</p>
        </div>
      </div>
      {shop?.message && <p className="mt-3 text-sm text-foreground" data-testid="shop-message">{shop.message}</p>}
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        {shop?.slots?.map((s, i) => (
          <span key={i} className="px-2.5 py-1 rounded-full bg-secondary text-foreground">{s.start} – {s.end}</span>
        ))}
        <span className="px-2.5 py-1 rounded-full bg-secondary text-muted-foreground">
          {shop?.days?.length === 7 ? "Tous les jours" : shop?.days?.map((d) => DAYS[d]).join(", ")}
        </span>
      </div>
    </div>
  );
}

export default function Shop() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("drive_cart") || "{}"); } catch { return {}; }
  });
  useEffect(() => { localStorage.setItem("drive_cart", JSON.stringify(cart)); }, [cart]);
  const [note, setNote] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const SLOTS = ["Avant mon arrivée (déposé dans le logement)", "09:00 – 10:00", "10:00 – 11:00", "11:00 – 12:00", "16:00 – 17:00", "17:00 – 18:00", "18:00 – 19:00"];
  const [listingId, setListingId] = useState("");
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data));
    api.get("/shop/products").then(({ data }) => setProducts(data));
  }, []);

  useEffect(() => {
    if (!user?.role) return;
    api.get("/bookings").then(({ data }) => setBookings(data.filter((b) => b.listing_id)));
    api.get("/shop/orders").then(({ data }) => setOrders(data.filter((o) => o.user_id === user.user_id)));
  }, [user]);

  const open = settings?.shop_open;
  const categories = useMemo(() => [...new Set(products.map((p) => p.category))], [products]);
  const lines = Object.entries(cart).map(([id, qty]) => ({ p: products.find((x) => x.product_id === id), qty })).filter((l) => l.p);
  const total = lines.reduce((s, l) => s + l.p.price * l.qty, 0);

  const add = (id, d) => setCart((c) => {
    const q = (c[id] || 0) + d;
    const n = { ...c };
    if (q <= 0) delete n[id]; else n[id] = q;
    return n;
  });

  const checkout = async () => {
    if (!user?.role) { navigate("/connexion"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/shop/orders", {
        items: lines.map((l) => ({ product_id: l.p.product_id, qty: l.qty })),
        listing_id: listingId || null, note, pickup_time: pickupTime, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-rose-brand mb-3">
          <ShoppingBasket className="w-4 h-4" /> Drive
        </span>
        <h1 className="font-serif text-4xl sm:text-5xl text-olive">Course en ligne</h1>
        <p className="text-muted-foreground mt-2 max-w-xl">Commandez vos courses avant ou pendant votre séjour, nous les déposons dans votre logement.</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          <div className="lg:col-span-2 space-y-8">
            {settings && <ShopHours shop={settings.shop} open={open} now={settings.now_paris} />}

            {products.length === 0 && <p className="text-muted-foreground" data-testid="shop-empty">Aucun produit pour le moment.</p>}
            {categories.map((cat) => (
              <section key={cat}>
                <h2 className="font-serif text-2xl text-olive mb-3">{cat}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`shop-category-${cat}`}>
                  {products.filter((p) => p.category === cat).map((p) => (
                    <div key={p.product_id} className="flex gap-3 p-3 rounded-2xl bg-white border border-border hover-lift" data-testid={`product-${p.product_id}`}>
                      <div className="w-20 h-20 rounded-xl bg-secondary overflow-hidden shrink-0 flex items-center justify-center">
                        {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : <ShoppingBasket className="w-6 h-6 text-olive/50" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-olive leading-tight">{p.name}</p>
                        <p className="text-xs text-muted-foreground">{p.unit}{p.stock != null && p.stock <= 5 && <span className="text-rose-brand ml-1" data-testid={`stock-low-${p.product_id}`}>· plus que {p.stock}</span>}</p>
                        <p className="font-bold text-olive mt-1">{p.price.toFixed(2).replace(".", ",")} €</p>
                      </div>
                      <div className="flex items-center gap-1 self-end">
                        {cart[p.product_id] ? (
                          <>
                            <button onClick={() => add(p.product_id, -1)} className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-secondary" data-testid={`minus-${p.product_id}`}><Minus className="w-3.5 h-3.5" /></button>
                            <span className="w-6 text-center font-semibold" data-testid={`qty-${p.product_id}`}>{cart[p.product_id]}</span>
                          </>
                        ) : null}
                        <button onClick={() => add(p.product_id, 1)} disabled={!open || (p.stock != null && (cart[p.product_id] || 0) >= p.stock)} className="w-8 h-8 rounded-full bg-rose-brand text-white flex items-center justify-center hover:bg-rosebrand-soft disabled:opacity-40" data-testid={`add-${p.product_id}`}><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <aside className="space-y-6">
            <div className="rounded-2xl bg-white border border-border p-5 sticky top-24" data-testid="cart-panel">
              <h2 className="font-serif text-2xl text-olive mb-3">Mon panier</h2>
              {lines.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="cart-empty">Votre panier est vide.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {lines.map((l) => (
                    <li key={l.p.product_id} className="flex justify-between items-center gap-2" data-testid={`cart-line-${l.p.product_id}`}>
                      <span className="truncate">{l.qty} × {l.p.name}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className="font-semibold">{(l.p.price * l.qty).toFixed(2).replace(".", ",")} €</span>
                        <button onClick={() => add(l.p.product_id, -l.qty)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="border-t border-border mt-4 pt-3 flex justify-between font-bold text-olive">
                <span>Total</span><span data-testid="cart-total">{total.toFixed(2).replace(".", ",")} €</span>
              </div>
              {bookings.length > 0 && (
                <div className="mt-4">
                  <label className="text-xs text-muted-foreground">Logement de retrait</label>
                  <Select value={listingId} onValueChange={setListingId}>
                    <SelectTrigger className="mt-1" data-testid="order-listing-select"><SelectValue placeholder="Choisir un séjour" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {[...new Map(bookings.map((b) => [b.listing_id, b])).values()].map((b) => (
                        <SelectItem key={b.listing_id} value={b.listing_id}>{b.listing_title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="mt-3">
                <label className="text-xs text-muted-foreground">Créneau de dépôt souhaité</label>
                <Select value={pickupTime} onValueChange={setPickupTime}>
                  <SelectTrigger className="mt-1" data-testid="order-pickup-select"><SelectValue placeholder="Choisir un créneau" /></SelectTrigger>
                  <SelectContent className="bg-white">{SLOTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note pour l'équipe (heure d'arrivée, allergies...)" className="mt-3 text-sm" data-testid="order-note-input" />
              <Button onClick={checkout} disabled={!open || lines.length === 0 || busy} className="w-full mt-4 bg-rose-brand hover:bg-rosebrand-soft text-white rounded-full h-11" data-testid="checkout-button">
                {!open ? "Drive fermé" : busy ? "Redirection..." : user?.role ? "Payer et commander" : "Se connecter pour commander"}
              </Button>
            </div>

            {user?.role && (
              <div className="rounded-2xl bg-white border border-border p-5" data-testid="my-orders">
                <h2 className="font-serif text-xl text-olive mb-3">Mes commandes</h2>
                {orders.length === 0 ? <p className="text-sm text-muted-foreground">Aucune commande.</p> : orders.map((o) => (
                  <div key={o.order_id} className="py-2 border-b border-border last:border-0 text-sm" data-testid={`order-${o.order_id}`}>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-olive">{o.total.toFixed(2).replace(".", ",")} €</span>
                      <Badge className={(o.payment_status === "refunded" ? ORDER_STATUS.refunded : ORDER_STATUS[o.status])?.cls}>{(o.payment_status === "refunded" ? ORDER_STATUS.refunded : ORDER_STATUS[o.status])?.label || o.status}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</p>
                  </div>
                ))}
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
